import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { ConversationService, type ConversationWithDetails, type Conversation } from '../services/conversations'
import { AuthService, type AuthUser } from '../services/auth'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import ChatInterface from '../components/ChatInterface'
import { ChatPromptService, type ChatPromptWithVideo } from '../services/chatPrompts'
import type { GenericConversationContext } from '../types/chat'
import chatIconMark from '../assets/icon/chat-icon-mark.svg'

// Router state accepted by this page: an initial message to auto-send (from the videos
// page composer) and the header's みらいワーク link asking for a fresh chat.
type ChatPageNavState = { initialMessage?: string; newChat?: boolean } | null

const ChatPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const rawInitialMessage = (location.state as ChatPageNavState)?.initialMessage
  const [pendingInitialMessage, setPendingInitialMessage] = useState<string | undefined>(rawInitialMessage)
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [conversations, setConversations] = useState<ConversationWithDetails[]>([])
  const [chatHistoryError, setChatHistoryError] = useState('')
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithDetails | null>(null)
  const [genericChatPrompt, setGenericChatPrompt] = useState<ChatPromptWithVideo | null>(null)
  const [isLoadingChatPrompt, setIsLoadingChatPrompt] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [newChatKey, setNewChatKey] = useState(0)
  // Opening the page starts a fresh chat instead of resuming the last conversation —
  // past conversations are reached through the history list. The one exception is
  // arriving with an initial message to send, which continues to open its own new chat.
  const [isNewChat, setIsNewChat] = useState(!rawInitialMessage)
  // Which conversation the chat area is actually on, including one created by sending
  // the first message of a new chat (that one never becomes selectedConversation).
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const loadChatHistory = useCallback(async (userId: string) => {
    try {
      setChatHistoryError('')
      const chatHistory = await ConversationService.getUserRecentConversations(userId)
      setConversations(chatHistory || [])
    } catch (error) {
      console.error('Failed to load chat history:', error)
      setChatHistoryError(error instanceof Error ? error.message : 'Unknown error')
      setConversations([])
    }
  }, [])

  const loadGenericChatPrompt = useCallback(async () => {
    try {
      setIsLoadingChatPrompt(true)
      const prompt = await ChatPromptService.getGenericChatPrompt()
      if (prompt) {
        setGenericChatPrompt(prompt)
      }
    } catch (error) {
      console.error('Failed to load generic chat prompt:', error)
    } finally {
      setIsLoadingChatPrompt(false)
    }
  }, [])

  // Clear the router state on mount so back-navigation doesn't replay it
  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true)
        const user = await AuthService.getCurrentUser()
        if (user) {
          setCurrentUser(user)
          await Promise.all([loadChatHistory(user.id), loadGenericChatPrompt()])
        }
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [loadChatHistory, loadGenericChatPrompt])

  const handleNewChat = () => {
    setSelectedConversation(null)
    setActiveConversationId(null)
    setPendingInitialMessage(undefined)
    setIsNewChat(true)
    setNewChatKey(k => k + 1)
  }

  // Clicking みらいワーク while already on /chat pushes a new history entry but doesn't
  // remount the page, so start the fresh chat here. Keyed on location.key to catch
  // repeat clicks; the state-clearing replace above lands here too but carries no state.
  const handledNavKeyRef = useRef(location.key)
  useEffect(() => {
    if (location.key === handledNavKeyRef.current) return
    handledNavKeyRef.current = location.key
    if (!(location.state as ChatPageNavState)?.newChat) return
    handleNewChat()
    navigate(location.pathname, { replace: true, state: null })
  }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleConversationSelect = (conversation: ConversationWithDetails) => {
    setSelectedConversation(conversation)
    setActiveConversationId(conversation.id)
    setIsNewChat(false)
  }

  const handleConversationChange = useCallback((conversation: Conversation | null) => {
    setIsNewChat(false)
    setActiveConversationId(conversation?.id ?? null)
    if (!conversation) return
    setPendingInitialMessage(undefined)
    setConversations(prev => {
      if (prev.some(c => c.id === conversation.id)) return prev
      const details: ConversationWithDetails = {
        ...conversation,
        user: { id: currentUser?.id || '', name: '', email: '' },
        chat_prompt: {
          id: genericChatPrompt?.id || '',
          title: conversation.title,
          is_generic: true,
          model: genericChatPrompt?.model || 'default',
          temperature: genericChatPrompt?.temperature,
          max_tokens: genericChatPrompt?.max_tokens,
          category: null
        }
      }
      return [details, ...prev]
    })
  }, [currentUser, genericChatPrompt])

  // Chats started from a video are titled after the video at creation, so the
  // conversation's own title is the right label once it can be renamed.
  const conversationLabel = (conversation: ConversationWithDetails) =>
    conversation.title || conversation.chat_prompt?.title || 'チャット'

  const startRename = (conversation: ConversationWithDetails) => {
    setMenuOpenId(null)
    setRenamingId(conversation.id)
    setRenameValue(conversationLabel(conversation))
  }

  const commitRename = async (conversation: ConversationWithDetails) => {
    const title = renameValue.trim()
    setRenamingId(null)
    if (!title || title === conversation.title) return

    const previousTitle = conversation.title
    setConversations(prev => prev.map(c => c.id === conversation.id ? { ...c, title } : c))
    try {
      await ConversationService.updateConversationTitle(conversation.id, title)
    } catch (error) {
      console.error('Failed to rename conversation:', error)
      setConversations(prev => prev.map(c => c.id === conversation.id ? { ...c, title: previousTitle } : c))
      setChatHistoryError(error instanceof Error ? error.message : '会話タイトルの更新に失敗しました')
    }
  }

  // Soft delete: the history list only shows active conversations, and keeping the
  // messages leaves the teacher/admin 会話監視 view intact.
  const handleDeleteConversation = async (conversation: ConversationWithDetails) => {
    setMenuOpenId(null)
    const confirmed = window.confirm(
      `「${conversationLabel(conversation)}」を削除してもよろしいですか？\nこの操作は取り消せません。`
    )
    if (!confirmed) return

    try {
      await ConversationService.updateConversationStatus(conversation.id, 'deleted')
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      setChatHistoryError(error instanceof Error ? error.message : '会話の削除に失敗しました')
      return
    }

    setConversations(prev => prev.filter(c => c.id !== conversation.id))
    // If the chat area is showing the deleted conversation, move it to a fresh chat
    // so the student can't keep writing into it.
    if (activeConversationId === conversation.id || selectedConversation?.id === conversation.id) {
      handleNewChat()
    }
  }

  // Close an open row menu on any click outside it
  useEffect(() => {
    if (!menuOpenId) return
    const close = () => setMenuOpenId(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [menuOpenId])

  const genericChatContext = useMemo<GenericConversationContext | null>(
    () => genericChatPrompt ? { type: 'generic', chatPrompt: genericChatPrompt } : null,
    [genericChatPrompt]
  )

  const activeContext = useMemo<GenericConversationContext | null>(
    () => selectedConversation?.chat_prompt
      ? {
          type: 'generic',
          chatPrompt: {
            ...selectedConversation.chat_prompt,
            video: null,
            category_id: selectedConversation.chat_prompt.category?.id || null,
            system_prompt: '',
            initial_message: null,
            // Reuse the prompt's own AI settings. Falling back to a concrete model
            // here (it used to be 'gpt-4') pins every resumed conversation to it and
            // ignores the admin default; 'gpt-4' also has an 8k context window, which
            // ai_max_tokens alone exhausts, so every such request failed outright.
            model: selectedConversation.chat_prompt.model || 'default',
            temperature: selectedConversation.chat_prompt.temperature ?? 0.7,
            max_tokens: selectedConversation.chat_prompt.max_tokens ?? 1000,
            is_active: true,
            created_at: '',
            updated_at: '',
            video_id: null
          } as ChatPromptWithVideo
        }
      : genericChatContext,
    [selectedConversation, genericChatContext]
  )

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-8">
      <div className="flex h-[calc(100dvh-10rem)] min-h-[360px] max-h-[600px] bg-white rounded-lg shadow">
        {/* Conversation Thread List */}
        <div className="w-60 lg:w-80 shrink-0 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="flex items-center gap-2">
              <img alt="" className="h-10 w-[45px] shrink-0" src={chatIconMark} />
              <span className="text-xl font-bold text-[#33333b]">みらいワーク</span>
            </h3>
            <button
              onClick={handleNewChat}
              className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[#b4c865] hover:opacity-90 rounded-lg transition-opacity"
            >
              <span>＋</span>
              <span>新しいチャット</span>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {chatHistoryError && (
              <div className="m-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700">{chatHistoryError}</p>
                <button
                  onClick={() => currentUser && loadChatHistory(currentUser.id)}
                  className="mt-2 text-sm text-yellow-800 underline hover:text-yellow-900"
                >
                  再試行
                </button>
              </div>
            )}

            {conversations.length === 0 ? (
              <div className="p-4 text-center">
                <div className="text-gray-400 text-lg mb-2">🤖</div>
                <p className="text-sm text-gray-600 mb-2">まだAIとの会話がありません</p>
                <Link
                  to="/videos"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  動画を見る
                </Link>
              </div>
            ) : (
              <div className="p-2">
                {conversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className={`relative rounded-lg mb-2 transition-colors duration-200 ${
                      selectedConversation?.id === conversation.id
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {renamingId === conversation.id ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => commitRename(conversation)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(conversation)
                          // Clearing the value too makes the blur that follows a no-op
                          if (e.key === 'Escape') { setRenamingId(null); setRenameValue('') }
                        }}
                        className="w-full p-3 text-sm font-medium text-gray-900 bg-white border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    ) : (
                      <div className="flex items-center">
                        <button
                          onClick={() => handleConversationSelect(conversation)}
                          className="flex-1 min-w-0 text-left p-3"
                        >
                          <span className="block text-sm font-medium text-gray-900 truncate">
                            {conversationLabel(conversation)}
                          </span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setMenuOpenId(prev => prev === conversation.id ? null : conversation.id)
                          }}
                          aria-haspopup="menu"
                          aria-expanded={menuOpenId === conversation.id}
                          title="メニュー"
                          className="flex-shrink-0 px-2 py-3 text-gray-500 hover:text-gray-800 rounded-lg"
                        >
                          …
                        </button>
                      </div>
                    )}

                    {menuOpenId === conversation.id && (
                      <div
                        role="menu"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-2 top-11 z-10 w-40 py-1 bg-white border border-gray-200 rounded-lg shadow-lg"
                      >
                        <button
                          role="menuitem"
                          onClick={() => startRename(conversation)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          名前を変更する
                        </button>
                        <button
                          role="menuitem"
                          onClick={() => handleDeleteConversation(conversation)}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          削除する
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Active Chat Area — min-w-0 so a wide Markdown table in a reply scrolls inside
            its bubble instead of stretching the chat past the screen on iPad */}
        <div className="flex-1 min-w-0 flex flex-col">
          {activeContext ? (
            /* showResetButton stays off: threads are removed from the … menu in the list */
            <ChatInterface
              key={selectedConversation?.id || `generic-${newChatKey}`}
              context={activeContext}
              currentUser={currentUser}
              height="flex-1"
              onConversationChange={handleConversationChange}
              forceNewConversation={selectedConversation === null && (isNewChat || !!pendingInitialMessage)}
              conversationId={selectedConversation?.id}
              initialMessage={selectedConversation === null && !isNewChat ? pendingInitialMessage : undefined}
            />
          ) : isLoadingChatPrompt ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-600">みらいワーク機能を読み込み中...</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-gray-400 text-2xl mb-4">🤖</div>
                <p className="text-gray-600 mb-4">みらいワークを開始してください</p>
                <Link
                  to="/videos"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  動画を見る
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatPage
